import React, { useCallback, useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    ClickAwayListener,
    InputAdornment,
    debounce,
} from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { Search } from '@mui/icons-material';

interface GridCompactViewProps<T> {
    data: T[];
    propsToFilter: { name: keyof T, type: 'string' | 'date' }[];
    propsToSort: { name: keyof T, displayName: string }[];
    headingField?: { name: keyof T, type: 'string' | 'date' };
    additionalFields?: (keyof T)[];
    maxListHeight?: number;
    searchPlaceholder?: string;
    noResultsText?: string;
    sxListWrapper?: object;
    sxCompactViewBox?: object;
    getCompactTemplate?: (item: T, index: number) => JSX.Element;
    onMobileListItemClick?: (item: T) => void;
}

export default function GridCompactView<T>({
    data,
    propsToFilter,
    propsToSort,
    headingField,
    additionalFields,
    searchPlaceholder,
    noResultsText,
    sxListWrapper,
    maxListHeight,
    sxCompactViewBox,
    getCompactTemplate,
    onMobileListItemClick,
}: GridCompactViewProps<T>) {
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredItems, setFilteredItems] = useState(data);
    const [sortConfig, setSortConfig] = useState<{ field?: keyof T; direction: 'asc' | 'desc' | 'none' }>({
        direction: 'none',
    });
    const { i18n, t } = useTranslation();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const applySort = useCallback(() => {
        if (sortConfig.field && sortConfig.direction !== 'none') {
            setFilteredItems((prev) => {
                return [...prev].sort((a, b) => {
                    if (sortConfig.field) {
                        const aValue = String(a[sortConfig.field] || '');
                        const bValue = String(b[sortConfig.field] || '');
                        if (sortConfig.direction === 'asc') return aValue.localeCompare(bValue);
                        if (sortConfig.direction === 'desc') return bValue.localeCompare(aValue);
                    }
                    return 0;
                });
            });
        } else {
            setFilteredItems(data);
        }
    }, [sortConfig, data]);

    const handleSortClick = (field: keyof T) => {
        setSortConfig((prev) => {
            if (prev.field === field) {
                if (prev.direction === 'asc') return { field, direction: 'desc' };
                if (prev.direction === 'desc') return { field: undefined, direction: 'none' };
            }
            return { field, direction: 'asc' };
        });
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    React.useEffect(() => {
        applySort();
    }, [sortConfig, applySort]);

    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!query) {
                setFilteredItems(data);
                return;
            }

            const startsWithMatches: T[] = [];
            const containsMatches: T[] = [];

            data.forEach(item => {
                let foundInStartsWith = false;

                for (const prop of propsToFilter) {
                    let value: string;

                    if (prop.type === 'date') {
                        value = new Date(item[prop.name] as string)
                            .toLocaleDateString(i18n.language)
                            .toLowerCase();
                    } else {
                        value = String(item[prop.name] || '').toLowerCase();
                    }

                    if (value.startsWith(query)) {
                        if (!foundInStartsWith) {
                            startsWithMatches.push(item);
                            foundInStartsWith = true;
                        }
                        break;
                    }
                }

                if (!foundInStartsWith) {
                    for (const prop of propsToFilter) {
                        let value: string;

                        if (prop.type === 'date') {
                            value = new Date(item[prop.name] as string)
                                .toLocaleDateString(i18n.language)
                                .toLowerCase();
                        } else {
                            value = String(item[prop.name] || '').toLowerCase();
                        }

                        if (value.includes(query)) {
                            containsMatches.push(item);
                            break;
                        }
                    }
                }
            });

            setFilteredItems([...startsWithMatches, ...containsMatches]);
        }, 500),
        [data, propsToFilter, i18n.language]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(e.target.value);
        debouncedSearch(query);
    };

    const renderItem = useCallback(
        (item: T, index: number) => {
            if (!headingField) {
                return getCompactTemplate?.(item, index) ?? '';
            } else {
                const value = item[headingField.name];
                return (
                    <Box>
                        {value != null && headingField.type === 'string' ? String(value) : ''}
                        {value != null && headingField.type === 'date' ? new Date(value as string).toLocaleDateString(i18n.language) : ''}
                        {additionalFields?.map((field) => (
                            <Typography key={field.toString()} variant="body2" color="textSecondary">
                                {item[field] as string}
                            </Typography>
                        ))}
                    </Box>
                );
            }
        },
        [headingField, getCompactTemplate, i18n.language, additionalFields]
    );

    return (
        <Box sx={{ width: '100%', overflow: 'hidden', ...sxListWrapper }}>
            <Box display="flex" alignItems="center" mb={2}>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={handleSearch}
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton sx={{ height: 30, width: 30 }}>
                                    <Search />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}

                />
                <ClickAwayListener onClickAway={handleCloseMenu}>
                    <Box>
                        <IconButton sx={{ background: sortConfig.direction !== 'none' ? '#ffc600' : 'transparent', borderRadius: 5, width: 30, height: 30, ml: 3 }} onClick={handleOpenMenu}>
                            <SwapVertIcon />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleCloseMenu}>
                            {propsToSort.map((prop) => (
                                <MenuItem key={String(prop.name)} onClick={() => handleSortClick(prop.name)}>
                                    {prop.displayName}
                                    <Box ml={1}>
                                        {sortConfig.field === prop.name && sortConfig.direction !== 'none' ? (
                                            sortConfig.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                        ) : <></>}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </ClickAwayListener>
            </Box>
            <Box overflow='auto' maxHeight={maxListHeight} sx={{ ...sxCompactViewBox }}>
                {filteredItems.length > 0 ? (
                    <Box >
                        {filteredItems.map((item, index) => (
                            <Box onClick={() => onMobileListItemClick?.(item)} key={index}>
                                {renderItem(item, index)}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 2 }}>
                        {noResultsText ?? t('noRecords')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
